import {AfterViewInit, ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {Router} from '@angular/router';
import {LoggedInCallback} from '../../service-auth/cognito.service';
import {RoleChangeListener, StartupService} from '../../service-ui/startup.service';
import {MatSidenav} from '@angular/material/sidenav';
import {MediaMatcher} from '@angular/cdk/layout';
import {Subject} from 'rxjs';
import {CarrierServiceMap} from '../../../model/CarrierServiceMap';
import {CarrierServiceMapService} from '../../service-data/carrier-service-map.service';
import {LanguagePipe} from '../../pipe/language.pipe';
import {CARRIER_SERVICE_LEVELS} from '../../util/carrier-service-levels';
import {ServiceProvider} from '../../../model/ServiceProvider';
import {Facility} from '../../../model/Facility';
import {ServiceProviderService} from '../../service-data/service-provider.service';
import {FacilityService} from '../../service-data/facility.service';
import {RateConfigDocumentProperty} from '../../service-data/rate-config.service';
import {
  SalesTaxConfig,
  SalesTaxExemptionService,
} from '../../service-data/sales-tax-exemption.service';

/**
 * Ecommerce connection type definition for default carrier service map management.
 * When adding a new EcommerceConnection.type:
 *   1. Add an entry here with the correct mapId
 *   2. Add the type to getDefaultMapId() in EcommerceConnectionComponent
 *   3. Add translations via LanguagePipe / i18n
 */
interface EcommerceTypeConfig {
  type: string;
  mapId: number;
}

@Component({
  selector: 'app-network-settings',
  templateUrl: './network-settings.component.html',
  styleUrls: ['./network-settings.component.css']
})
export class NetworkSettingsComponent implements OnDestroy, OnInit, LoggedInCallback, AfterViewInit, RoleChangeListener {
  @ViewChild('mainSideNav', {static: true}) mainSideNav: MatSidenav;
  mobileQuery: MediaQueryList;
  private _mobileQueryListener: () => void;
  onDestroySubject: Subject<boolean> = new Subject();

  carrierServiceMapLoading = false;
  carrierServiceMapLoaded = false;

  /** Registered ecommerce types and their default map IDs */
  ecommerceTypes: EcommerceTypeConfig[] = [
    {type: 'SHOPIFY', mapId: 1},
    {type: 'WOOCOMMERCE', mapId: 2}
  ];

  /** Mappings grouped by type */
  mappingsByType: { [type: string]: CarrierServiceMap[] } = {};
  dirtyMappings: Set<CarrierServiceMap> = new Set();
  mappingSnapshots: Map<CarrierServiceMap, string> = new Map();

  carrierServiceLevels = CARRIER_SERVICE_LEVELS;

  // ── Rate-configuration editor state ──────────────────────────────────
  rateConfigPanelLoaded = false;
  serviceProviders: ServiceProvider[] = [];
  facilities: Facility[] = [];
  /** Current SP picked in the Service Provider rate-config panel. */
  selectedServiceProvider: ServiceProvider;
  /** Which SP property the editor is targeting. */
  selectedSpProperty: RateConfigDocumentProperty = 'rateConfig';
  /** Current Facility picked in the Facility rate-config panel. */
  selectedFacility: Facility;
  /**
   * Independent picker state for the parallel "Service Provider Packing
   * Rate Configurations" panel. Keeping it separate from
   * {@link selectedServiceProvider} lets the user switch SPs in one
   * panel without disturbing the other (the production and packing
   * rates can be tuned in parallel by different network admins).
   */
  selectedServiceProviderPacking: ServiceProvider;
  /** Which SP packing property the editor is targeting. */
  selectedSpPackingProperty: RateConfigDocumentProperty = 'packingRateConfig';
  /** Current Facility picked in the Facility packing-rate-config panel. */
  selectedFacilityPacking: Facility;

  // ── Sales Tax panel state ──────────────────────────────────────────
  salesTaxPanelLoaded = false;
  salesTaxSelectedSp: ServiceProvider | null = null;
  salesTaxConfig: SalesTaxConfig | null = null;
  salesTaxConfigLoading = false;
  salesTaxConfigSaving = false;
  salesTaxConfigError = '';
  /** Working copies; persisted to the server on "Save Config". */
  stxNexusStates: string[] = [];
  stxReciprocityOther: string[] = [];
  stxReciprocityWholesale: string[] = [];

  // ── CollectionProvider panel state ─────────────────────────────────
  // The panel picks a ServiceProvider, follows its
  // `collectionprovider_ID` through the payment-routing chain, and
  // hands the resolved CollectionProvider id to the shared
  // {@link CollectionProviderEditorComponent}. The SP list is
  // re-used from the rate-config / sales-tax pickers; this panel's
  // opened-handler kicks the SP fetch when the panel is opened first.
  collectionProviderPanelLoaded = false;
  collectionProviderSelectedSp: ServiceProvider | null = null;

  constructor(
    public router: Router,
    public startupService: StartupService,
    private carrierServiceMapService: CarrierServiceMapService,
    private serviceProviderService: ServiceProviderService,
    private facilityService: FacilityService,
    private salesTaxExemptionService: SalesTaxExemptionService,
    public changeDetectorRef: ChangeDetectorRef,
    public media: MediaMatcher
  ) {
    this.mobileQuery = media.matchMedia('(max-width: 600px)');
    this._mobileQueryListener = () => changeDetectorRef.detectChanges();
    this.mobileQuery.addEventListener('change', this._mobileQueryListener);
  }

  ngOnInit() {
    this.startupService.isAuthenticated(this);
  }

  ngAfterViewInit() {}

  ngOnDestroy() {
    this.onDestroySubject.next(true);
    this.mobileQuery.removeEventListener('change', this._mobileQueryListener);
  }

  isLoggedIn(message: string, isLoggedIn: boolean) {
    if (!isLoggedIn) {
      this.router.navigate(['/home/login']);
    }
  }

  roleChanged(role: string, roleId: string) {}

  getTypeLabel(type: string): string {
    return LanguagePipe.get(type);
  }

  onCarrierMapPanelOpened(): void {
    if (!this.carrierServiceMapLoaded) {
      this.carrierServiceMapLoading = true;
      this.carrierServiceMapService.getItems(true).subscribe(() => {
        this.carrierServiceMapLoading = false;
        this.carrierServiceMapLoaded = true;
        this.loadAllMappings();
      });
    }
  }

  loadAllMappings(): void {
    this.dirtyMappings.clear();
    this.mappingSnapshots.clear();
    for (const ec of this.ecommerceTypes) {
      this.mappingsByType[ec.type] = this.carrierServiceMapService.objects
        .filter(csm => csm.map_Id === ec.mapId);
      for (const m of this.mappingsByType[ec.type]) {
        this.snapshotMapping(m);
      }
    }
  }

  getMappings(type: string): CarrierServiceMap[] {
    return this.mappingsByType[type] || [];
  }

  getMapId(type: string): number {
    const config = this.ecommerceTypes.find(e => e.type === type);
    return config ? config.mapId : 0;
  }

  addMapping(type: string): void {
    const mapId = this.getMapId(type);
    const newMapping = new CarrierServiceMap({
      org_ID: 0,
      map_Id: mapId,
      customerCarrierService: '',
      carrierService: this.carrierServiceLevels[0].ID
    });
    this.carrierServiceMapService.addItem(newMapping).subscribe(added => {
      if (!this.mappingsByType[type]) {
        this.mappingsByType[type] = [];
      }
      this.mappingsByType[type].push(added);
      this.snapshotMapping(added);
      this.dirtyMappings.add(added);
    });
  }

  deleteMapping(type: string, mapping: CarrierServiceMap): void {
    this.carrierServiceMapService.deleteItem(mapping).subscribe(() => {
      const list = this.mappingsByType[type];
      if (list) {
        const idx = list.indexOf(mapping);
        if (idx !== -1) {
          list.splice(idx, 1);
        }
      }
      this.dirtyMappings.delete(mapping);
      this.mappingSnapshots.delete(mapping);
    });
  }

  saveMapping(mapping: CarrierServiceMap): void {
    this.carrierServiceMapService.updateItem(mapping).subscribe(() => {
      this.dirtyMappings.delete(mapping);
      this.snapshotMapping(mapping);
    });
  }

  snapshotMapping(mapping: CarrierServiceMap): void {
    this.mappingSnapshots.set(mapping, JSON.stringify({
      customerCarrierService: mapping.customerCarrierService,
      carrierService: mapping.carrierService
    }));
  }

  markMappingDirty(mapping: CarrierServiceMap): void {
    const snapshot = this.mappingSnapshots.get(mapping);
    const current = JSON.stringify({
      customerCarrierService: mapping.customerCarrierService,
      carrierService: mapping.carrierService
    });
    if (snapshot && snapshot === current) {
      this.dirtyMappings.delete(mapping);
    } else {
      this.dirtyMappings.add(mapping);
    }
  }

  isDuplicateCustomerCode(type: string, mapping: CarrierServiceMap): boolean {
    const list = this.mappingsByType[type] || [];
    return list.some(m =>
      m !== mapping && m.customerCarrierService === mapping.customerCarrierService && mapping.customerCarrierService !== ''
    );
  }

  // ── Rate-config editor wiring ───────────────────────────────────────
  onRateConfigPanelOpened(): void {
    if (this.rateConfigPanelLoaded) { return; }
    this.rateConfigPanelLoaded = true;
    // Filter on `active === true` so the rate-config editor pickers
    // never surface a retired ServiceProvider or Facility. Inactive
    // entities default to `active = true` (legacy data without the
    // column, or the server sending a row predating the migration),
    // so an explicit `=== false` test keeps backwards compatibility.
    // See docs/contracts/shared/ActiveFlags.md.
    this.serviceProviderService.getServiceProviders().subscribe(list => {
      this.serviceProviders = (list || []).filter(sp => sp && sp.active !== false);
    });
    this.facilities = (this.facilityService.objects || [])
      .filter(f => f && f.active !== false)
      .slice();
  }

  selectServiceProvider(sp: ServiceProvider): void {
    this.selectedServiceProvider = sp;
  }

  selectSpProperty(prop: RateConfigDocumentProperty): void {
    this.selectedSpProperty = prop;
  }

  selectFacility(f: Facility): void {
    this.selectedFacility = f;
  }

  // ── Packing-rate panel pickers (parallel to the production rate ones) ──

  selectServiceProviderPacking(sp: ServiceProvider): void {
    this.selectedServiceProviderPacking = sp;
  }

  selectSpPackingProperty(prop: RateConfigDocumentProperty): void {
    this.selectedSpPackingProperty = prop;
  }

  selectFacilityPacking(f: Facility): void {
    this.selectedFacilityPacking = f;
  }

  // ── Sales Tax panel wiring ──────────────────────────────────────────

  /** Lazy-load active SPs the first time the panel opens (re-uses the rate-config list when present). */
  onSalesTaxPanelOpened(): void {
    if (this.salesTaxPanelLoaded) { return; }
    this.salesTaxPanelLoaded = true;
    if (!this.serviceProviders || this.serviceProviders.length === 0) {
      this.serviceProviderService.getServiceProviders().subscribe(list => {
        this.serviceProviders = (list || []).filter(sp => sp && sp.active !== false);
      });
    }
  }

  selectSalesTaxSp(sp: ServiceProvider): void {
    this.salesTaxSelectedSp = sp;
    this.salesTaxConfig = null;
    this.salesTaxConfigError = '';
    if (!sp) { return; }
    this.loadSalesTaxConfig(sp.ID);
  }

  private loadSalesTaxConfig(spId: number): void {
    this.salesTaxConfigLoading = true;
    this.salesTaxConfigError = '';
    this.salesTaxExemptionService.getSpConfig(spId).subscribe(
      cfg => {
        this.salesTaxConfig = cfg;
        this.stxNexusStates = (cfg.nexusStates || []).slice();
        this.stxReciprocityOther = (cfg.reciprocityOther || []).slice();
        this.stxReciprocityWholesale = (cfg.reciprocityWholesale || []).slice();
        this.salesTaxConfigLoading = false;
      },
      err => {
        console.error('NetworkSettings: loadSalesTaxConfig failed', err);
        this.salesTaxConfigLoading = false;
        this.salesTaxConfigError = this.errorMessage(err,
          LanguagePipe.get('Failed to load Sales Tax configuration.'));
      }
    );
  }

  saveSalesTaxConfig(): void {
    if (!this.salesTaxSelectedSp) { return; }
    this.salesTaxConfigSaving = true;
    this.salesTaxConfigError = '';
    const spId = this.salesTaxSelectedSp.ID;
    this.salesTaxExemptionService.saveSpConfig(spId, {
      nexusStates: this.stxNexusStates,
      reciprocityOther: this.stxReciprocityOther,
      reciprocityWholesale: this.stxReciprocityWholesale,
    }).subscribe(
      cfg => {
        this.salesTaxConfig = cfg;
        this.salesTaxConfigSaving = false;
      },
      err => {
        console.error('NetworkSettings: saveSalesTaxConfig failed', err);
        this.salesTaxConfigSaving = false;
        this.salesTaxConfigError = this.errorMessage(err,
          LanguagePipe.get('Failed to save Sales Tax configuration.'));
      }
    );
  }

  private errorMessage(err: any, fallback: string): string {
    if (err && err.error && typeof err.error === 'object' && err.error.error) {
      return String(err.error.error);
    }
    if (err && err.message) { return String(err.message); }
    return fallback;
  }

  // ── CollectionProvider panel wiring ────────────────────────────────

  /** Lazy-load active SPs the first time this panel opens (shared with the other panels). */
  onCollectionProviderPanelOpened(): void {
    if (this.collectionProviderPanelLoaded) { return; }
    this.collectionProviderPanelLoaded = true;
    if (!this.serviceProviders || this.serviceProviders.length === 0) {
      this.serviceProviderService.getServiceProviders().subscribe(list => {
        this.serviceProviders = (list || []).filter(sp => sp && sp.active !== false);
      });
    }
  }

  selectCollectionProviderSp(sp: ServiceProvider | null): void {
    this.collectionProviderSelectedSp = sp;
  }

  /**
   * Resolved CollectionProvider id for the currently-selected SP.
   * Returns `null` when no SP is picked OR the SP has no
   * `collectionprovider_ID` set (in which case the template shows a
   * "no mapping" hint instead of the editor).
   */
  get collectionProviderIdForSelectedSp(): number | null {
    const sp = this.collectionProviderSelectedSp;
    if (!sp || !sp.collectionprovider_ID) { return null; }
    return sp.collectionprovider_ID;
  }
}

